import type { ReactElement } from 'react';

import { useContent, useLocale } from '~/hooks/useContent';
import { localePrivacyPath, localeTermsPath, localeToPath } from '~/i18n/config';
import { visibleNavLinks } from '~/lib/navLinks';
import { Container } from '~/components/ui/Container';
import { LanguageSwitcher } from './LanguageSwitcher';

import logoUrl from '~/assets/logos/arpa-logo.svg';

/**
 * Full-width footer with four column groups on desktop, stacked on mobile:
 * 1. Brand — logo + tagline
 * 2. Quick links — same 6 nav anchor links
 * 3. Contact — address, phone, email
 * 4. Legal — privacy / terms links + LanguageSwitcher
 *
 * Bottom bar: copyright (supports optional `{year}` placeholder replacement).
 */
function Footer(): ReactElement {
  const content = useContent();
  const locale = useLocale();
  const currentYear = new Date().getFullYear();
  const quickLinks = visibleNavLinks(content.nav.links, { portfolio: __FEATURE_PORTFOLIO__ });

  const copyright = content.footer.copyright.replace('{year}', String(currentYear));
  const homeHref = localeToPath(locale);
  const privacyHref = localePrivacyPath(locale);
  const termsHref = localeTermsPath(locale);

  return (
    <footer
      aria-labelledby="footer-heading"
      className="bg-[var(--color-deep-panel)] border-t border-[rgba(232,236,244,0.05)]"
    >
      {/* Visually-hidden heading for landmark labelling */}
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>

      <Container className="py-16 md:py-20">
        {/* Four-column grid */}
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {/* ── Column 1: Brand ─────────────────────────────────────── */}
          <div className="flex flex-col gap-4 lg:col-span-1">
            <a
              href={homeHref}
              aria-label="ARPA — home"
              className="inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
            >
              <img src={logoUrl} alt="ARPA" className="h-9 w-auto" />
            </a>
            <p className="text-sm leading-relaxed text-[var(--color-steel-mist)] max-w-[22ch]">
              {content.footer.tagline}
            </p>
          </div>

          {/* ── Column 2: Quick links ───────────────────────────────── */}
          <div>
            <p className="mb-4 text-xs font-display font-semibold tracking-[0.2em] uppercase text-[var(--color-frost-white)]">
              {content.footer.quickLinks}
            </p>
            <ul className="flex flex-col gap-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={`#${link.id}`}
                    className="text-sm text-[var(--color-steel-mist)] hover:text-[var(--color-frost-white)] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Column 3: Contact ───────────────────────────────────── */}
          <div>
            <p className="mb-4 text-xs font-display font-semibold tracking-[0.2em] uppercase text-[var(--color-frost-white)]">
              {content.contact.heading}
            </p>
            <address className="not-italic flex flex-col gap-3 text-sm text-[var(--color-steel-mist)]">
              {/* Postal address — multi-line for clarity */}
              <span className="flex flex-col gap-0.5 leading-[1.5]">
                <span className="text-[var(--color-frost-white)]">
                  {content.contact.details.companyName}
                </span>
                <span>{content.contact.details.street}</span>
                <span>
                  {content.contact.details.postalCode} {content.contact.details.city}
                </span>
              </span>
              <a
                href={`tel:${content.contact.details.phone.replace(/\s/g, '')}`}
                className="text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
              >
                {content.contact.details.phone}
              </a>
              <a
                href={`mailto:${content.contact.details.email}`}
                className="text-[var(--color-frost-white)] hover:text-[var(--color-cyan-signal)] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
              >
                {content.contact.details.email}
              </a>
              {/* Compact legal registration line — required for Polish B2B disclosure */}
              <span className="mt-1 text-xs text-[var(--color-steel-mist)]/80 leading-relaxed">
                {content.contact.details.nipLabel} {content.contact.details.nip} ·{' '}
                {content.contact.details.krsLabel} {content.contact.details.krs}
              </span>
            </address>
          </div>

          {/* ── Column 4: Legal + LanguageSwitcher ─────────────────── */}
          <div>
            <p className="mb-4 text-xs font-display font-semibold tracking-[0.2em] uppercase text-[var(--color-frost-white)]">
              {content.footer.legal}
            </p>
            <ul className="flex flex-col gap-2 mb-6">
              <li>
                <a
                  href={privacyHref}
                  className="text-sm text-[var(--color-steel-mist)] hover:text-[var(--color-frost-white)] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
                >
                  {content.footer.privacy}
                </a>
              </li>
              <li>
                <a
                  href={termsHref}
                  className="text-sm text-[var(--color-steel-mist)] hover:text-[var(--color-frost-white)] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan-signal)] rounded-sm"
                >
                  {content.footer.terms}
                </a>
              </li>
            </ul>
            <LanguageSwitcher />
          </div>
        </div>

        {/* ── Bottom bar ──────────────────────────────────────────────── */}
        <div className="mt-12 pt-8 border-t border-[rgba(232,236,244,0.05)]">
          <p className="text-xs text-[var(--color-steel-mist)]">{copyright}</p>
        </div>
      </Container>
    </footer>
  );
}

export { Footer };
export default Footer;
